import { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { RootState } from "@src/reducers/store";
import { Dashboard } from "@src/types/dashboards";
import { normalizeDashboard } from "@src/reducers/dashboards-slice";
import { getDashboardsV2UseCase } from "@src/cloud-connect/dashboards-v2/use-cases/get-dashboards-v2-use-case";
import { getDashboardByIdV2UseCase } from "@src/cloud-connect/dashboards-v2/use-cases/get-dashboard-by-id-v2-use-case";

export type ViewerDashboardState =
  | { status: "loading" }
  | { status: "no-dashboards" }
  | { status: "no-default" }
  | { status: "draft" }
  | { status: "error"; error: string }
  | { status: "ready"; dashboard: Dashboard };

/**
 * Loads the dashboard for the read-only `/dashboard` viewer.
 *
 * With no `dashboardId` (the plain `/dashboard` URL) it loads the published
 * default: list → find `is_default` → that dashboard's full detail. This is
 * the original behavior, unchanged.
 *
 * With a `dashboardId` (the `/dashboard/:dashboardId` URL) it skips the list
 * and fetches that dashboard directly. A draft is refused here so a
 * hand-written URL cannot expose an unpublished dashboard. A wrong id comes
 * back as an error (the BE 404s), which the page renders as its error view.
 *
 * This hook is deliberately independent of the editor: it does not read the
 * shared `dashboards` slice, does not touch `selectedDashboardId`, and writes
 * nothing to Redux.
 */
export const useViewerDashboard = (
  dashboardId?: string,
): ViewerDashboardState => {
  const authContext = useSelector(
    (state: RootState) => state.yourSlice.authContext,
    shallowEqual,
  );
  const [state, setState] = useState<ViewerDashboardState>({
    status: "loading",
  });

  useEffect(() => {
    if (!authContext) {
      setState({ status: "loading" });
      return;
    }

    let cancelled = false;
    setState({ status: "loading" });

    (async () => {
      try {
        if (dashboardId) {
          const detail = await getDashboardByIdV2UseCase(dashboardId);
          if (cancelled) return;
          if (!detail.success || !detail.data) {
            setState({
              status: "error",
              error: detail.error ?? "Failed to fetch dashboard",
            });
            return;
          }
          if (detail.data.status !== "published") {
            setState({ status: "draft" });
            return;
          }
          // Same normalization the editor applies to every response: re-keys
          // `positioning` to dashed widget ids and defaults widgets/columnCount,
          // so the grid lays out identically on both pages.
          setState({
            status: "ready",
            dashboard: normalizeDashboard(detail.data),
          });
          return;
        }

        const list = await getDashboardsV2UseCase();
        if (cancelled) return;
        if (!list.success || !list.data) {
          setState({
            status: "error",
            error: list.error ?? "Failed to fetch dashboards",
          });
          return;
        }
        if (list.data.length === 0) {
          setState({ status: "no-dashboards" });
          return;
        }

        const defaultSummary = list.data.find((d) => d.is_default);
        if (!defaultSummary) {
          setState({ status: "no-default" });
          return;
        }

        const detail = await getDashboardByIdV2UseCase(defaultSummary.id);
        if (cancelled) return;
        if (!detail.success || !detail.data) {
          setState({
            status: "error",
            error: detail.error ?? "Failed to fetch dashboard",
          });
          return;
        }

        setState({
          status: "ready",
          dashboard: normalizeDashboard(detail.data),
        });
      } catch (error) {
        if (cancelled) return;
        setState({
          status: "error",
          error: error instanceof Error ? error.message : "Unknown error",
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authContext, dashboardId]);

  return state;
};
